import { Button, Stack, Typography, useTheme } from "@mui/material";
import { ActivitiesFilterDateItem } from "web_myplann/Activities/Filters/Components/ActivitiesFilterDateItem";
import {
	DateRange,
	type DateRangeType,
	getDateRangeByType,
} from "web_myplann/Activities/Filters/Helpers/getDateRangeByType";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DateTimePicker } from "web_myplann/Shared/Components/DateTimePicker";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import {
	activitiesFiltersState,
	defaultActivitiesFiltersState,
} from "web_myplann/Shared/State/activitiesFiltersAtom";
import { DEFAULT_ROUTE } from "web_myplann/Utils/constants";
import "dayjs/locale/cs";
import { useAtomValue, useSetAtom } from "jotai";
import { RotateCw } from "lucide-react";
import { type FC, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

export const ActivitiesFilterDatePage: FC = () => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();

	const { dateFrom: defaultDateFrom, dateTo: defaultDateTo } = useAtomValue(
		activitiesFiltersState,
	);
	const setFiltersState = useSetAtom(activitiesFiltersState);

	const [dateFrom, setDateFrom] = useState<Date | null>(defaultDateFrom);
	const [dateTo, setDateTo] = useState<Date | null>(defaultDateTo);

	const setDates = useCallback(
		(type: DateRangeType) => {
			const { from, to } = getDateRangeByType(type);
			setDateFrom(from);
			setDateTo(to);
		},
		[setDateFrom, setDateTo],
	);

	const handleReset = useCallback(() => {
		setDateFrom(defaultActivitiesFiltersState.dateFrom);
		setDateTo(defaultActivitiesFiltersState.dateTo);

		setFiltersState((prev) => ({
			...prev,
			dateFrom: defaultActivitiesFiltersState.dateFrom,
			dateTo: defaultActivitiesFiltersState.dateTo,
		}));
	}, [setDateFrom, setDateTo, setFiltersState]);

	const handleAccept = () => {
		setFiltersState((prev) => ({
			...prev,
			dateFrom,
			dateTo,
		}));

		navigate(-1);
	};

	return (
		<MyPlannLayout
			topBar={
				<NavigationHeader
					title={t(Resources.Activity.Filters.Term.title)}
					defaultRoute={DEFAULT_ROUTE}
					endComponent={<RotateCw onClick={handleReset} />}
				/>
			}
		>
			<Stack paddingTop={1.6} gap={1.6}>
				<Typography
					fontSize={16}
					fontWeight={600}
					color={theme.palette.grey[700]}
				>
					{t(Resources.Activity.Filters.Term.CustomPeriod)}
				</Typography>
				<Stack
					direction="row"
					justifyContent={"space-between"}
					alignItems={"center"}
					gap={1.2}
				>
					<DateTimePicker
						label={t(Resources.Activity.Filters.Term.From)}
						onChange={setDateFrom}
						value={dateFrom}
						clearable
					/>
					<DateTimePicker
						label={t(Resources.Activity.Filters.Term.To)}
						onChange={setDateTo}
						value={dateTo}
						clearable
					/>
				</Stack>
				<Typography
					fontSize={16}
					fontWeight={600}
					color={theme.palette.grey[700]}
				>
					{t(Resources.Activity.Filters.Term.ChoosePeriod)}
				</Typography>
				<Stack gap={1.6}>
					<ActivitiesFilterDateItem
						text={t(Resources.Activity.Filters.Term.WithoutPeriod)}
						onClick={() => setDates(DateRange.empty)}
					/>
					<ActivitiesFilterDateItem
						text={t(Resources.Activity.Filters.Term.Tomorrow)}
						onClick={() => setDates(DateRange.tomorrow)}
					/>
					<ActivitiesFilterDateItem
						text={t(Resources.Activity.Filters.Term.Today)}
						onClick={() => setDates(DateRange.today)}
					/>
					<ActivitiesFilterDateItem
						text={t(Resources.Activity.Filters.Term.Expired)}
						onClick={() => setDates(DateRange.expired)}
					/>
				</Stack>
				<Button
					variant="contained"
					color="primary"
					type="button"
					fullWidth
					onClick={handleAccept}
				>
					{t(Resources.Common.Apply)}
				</Button>
			</Stack>
		</MyPlannLayout>
	);
};
